import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Edit, Plus, Trash2, X, Image, ArrowLeft, ArrowRightLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SingleSubRoom = ({ cageNumber, roomLabel, animals, getAuthHeaders, onClose, onRefresh, allAnimals = [], onMoveDialogChange, isAdmin = false }) => {
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(false);
  const [animalDialogOpen, setAnimalDialogOpen] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [animalToMove, setAnimalToMove] = useState(null);
  const [moveDestination, setMoveDestination] = useState({ cage_number: '', sub_room: '' });
  const [availableSubRooms, setAvailableSubRooms] = useState([]);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearingRoom, setClearingRoom] = useState(false);
  const [animalFormData, setAnimalFormData] = useState({
    animal_type: "rabbit",
    breed: "",
    age: "",
    gender: "male",
    purchase_date: "",
    birth_date: "",
    weight: "",
    health_status: "healthy",
    cage_number: cageNumber,
    sub_room: roomLabel,
    photo: "",
    group: "stock"
  });

  // Calculate age in months from birth date
  const calculateAgeFromBirthDate = (birthDate) => {
    if (!birthDate) return '';
    const birth = new Date(birthDate);
    const today = new Date();
    
    // Calculate difference in months
    let months = (today.getFullYear() - birth.getFullYear()) * 12;
    months += today.getMonth() - birth.getMonth();
    
    // Adjust for day of month
    if (today.getDate() < birth.getDate()) {
      months--;
    }
    
    // Return as decimal with one decimal place (e.g., 3.5 months)
    const daysDiff = (today.getDate() - birth.getDate());
    const partialMonth = daysDiff > 0 ? (daysDiff / 30) : 0;
    const totalMonths = Math.max(0, months + partialMonth);
    
    return totalMonths.toFixed(1);
  };

  // Available cages and sub-rooms for move functionality
  const CAGES = ['C-01', 'C-02', 'C-03', 'C-04', 'C-05', 'C-06', 'C-07', 'C-08', 'C-09', 'C-10'];
  const SUB_ROOMS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

  // Get empty sub-rooms for a given cage
  const getEmptySubRooms = (targetCage) => {
    const occupiedRooms = allAnimals
      .filter(a => a.cage_number === targetCage)
      .map(a => a.sub_room);
    return SUB_ROOMS.filter(room => !occupiedRooms.includes(room));
  };

  // Group colors for badges
  const GROUP_COLORS = {
    stock: { bg: '#3B82F6', label: 'Stock' },
    babies: { bg: '#EC4899', label: 'Babies' },
    mothers: { bg: '#8B5CF6', label: 'Mothers' },
    crossed: { bg: '#F59E0B', label: 'Crossed' },
    pregnant: { bg: '#EF4444', label: 'Pregnant' },
    weaning: { bg: '#14B8A6', label: 'Weaning' },
    sold: { bg: '#6B7280', label: 'Sold' },
    ready_to_sell: { bg: '#10B981', label: 'Ready to Sell' }
  };
  const [tempRoomData, setTempRoomData] = useState({
    capacity: 10,
    temperature: "",
    humidity: "",
    notes: ""
  });
  const animalPhotoRef = React.useRef(null);

  useEffect(() => {
    fetchRoomData();
  }, [cageNumber, roomLabel]);

  const fetchRoomData = async () => {
    try {
      const response = await axios.get(`${API}/sub-rooms?cage_number=${cageNumber}`, { headers: getAuthHeaders() });
      const room = response.data.find(r => r.room_label === roomLabel);
      
      if (!room) {
        // Create the room if it doesn't exist
        await axios.post(`${API}/sub-rooms`, {
          cage_number: cageNumber,
          room_label: roomLabel,
          capacity: 10
        }, { headers: getAuthHeaders() });
        fetchRoomData();
      } else {
        setRoomData(room);
        setTempRoomData({
          capacity: room.capacity || 10,
          temperature: room.temperature || "",
          humidity: room.humidity || "",
          notes: room.notes || ""
        });
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching room data:", error);
      setLoading(false);
    }
  };

  const handleSaveRoomDetails = async () => {
    try {
      await axios.put(
        `${API}/sub-rooms/${cageNumber}/${roomLabel}`,
        tempRoomData,
        { headers: getAuthHeaders() }
      );
      toast.success("Room details updated");
      setEditingRoom(false);
      fetchRoomData();
    } catch (error) {
      toast.error("Failed to update room details");
    }
  };

  const getRoomAnimals = () => {
    return animals.filter(a => a.sub_room === roomLabel && a.cage_number === cageNumber);
  };

  const handleAddAnimal = () => {
    setEditingAnimal(null);
    setAnimalFormData({
      animal_type: "rabbit",
      breed: "",
      age: "",
      gender: "male",
      purchase_date: "",
      birth_date: "",
      weight: "",
      health_status: "healthy",
      cage_number: cageNumber,
      sub_room: roomLabel,
      photo: "",
      group: "stock"
    });
    setAnimalDialogOpen(true);
  };

  const handleEditAnimal = (animal) => {
    setEditingAnimal(animal);
    setAnimalFormData({
      animal_type: animal.animal_type,
      breed: animal.breed,
      age: animal.age,
      gender: animal.gender || "male",
      purchase_date: animal.purchase_date || "",
      birth_date: animal.birth_date || "",
      weight: animal.weight,
      health_status: animal.health_status,
      cage_number: animal.cage_number,
      sub_room: animal.sub_room || "",
      photo: animal.photo || "",
      group: animal.group
    });
    setAnimalDialogOpen(true);
  };

  const handleDeleteAnimal = async (animalId) => {
    if (window.confirm("Are you sure you want to delete this animal?")) {
      try {
        await axios.delete(`${API}/animals/${animalId}`, { headers: getAuthHeaders() });
        toast.success("Animal deleted successfully");
        onRefresh();
        fetchRoomData();
      } catch (error) {
        toast.error("Failed to delete animal");
      }
    }
  };

  const handleMoveAnimal = (animal) => {
    setAnimalToMove(animal);
    setMoveDestination({ cage_number: '', sub_room: '' });
    setAvailableSubRooms([]);
    setMoveDialogOpen(true);
    // Notify parent that move dialog is open (pauses auto-refresh)
    if (onMoveDialogChange) onMoveDialogChange(true);
  };

  const handleCageChange = (newCage) => {
    setMoveDestination({ cage_number: newCage, sub_room: '' });
    const emptyRooms = getEmptySubRooms(newCage);
    setAvailableSubRooms(emptyRooms);
  };

  const handleMoveSubmit = async () => {
    if (!moveDestination.cage_number || !moveDestination.sub_room) {
      toast.error("Please select both cage and sub-room");
      return;
    }
    
    if (moveDestination.cage_number === animalToMove.cage_number && moveDestination.sub_room === animalToMove.sub_room) {
      toast.error("Animal is already in this location");
      return;
    }

    try {
      await axios.put(`${API}/animals/${animalToMove.id}`, {
        cage_number: moveDestination.cage_number,
        sub_room: moveDestination.sub_room
      }, { headers: getAuthHeaders() });
      
      toast.success(`Animal moved to ${moveDestination.cage_number} - Room ${moveDestination.sub_room}`);
      setMoveDialogOpen(false);
      setAnimalToMove(null);
      // Notify parent that move dialog is closed
      if (onMoveDialogChange) onMoveDialogChange(false);
      onRefresh();
      fetchRoomData();
    } catch (error) {
      toast.error("Failed to move animal");
      console.error(error);
    }
  };

  const handleClearSubRoom = async () => {
    setClearingRoom(true);
    try {
      const response = await axios.delete(
        `${API}/sub-rooms/${cageNumber}/${roomLabel}/clear`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.pending) {
        // Non-admin: request submitted for approval
        toast.success(`Clear request submitted for admin approval (${response.data.animal_count} animals)`);
      } else {
        // Admin: cleared immediately
        toast.success(response.data.message);
      }
      
      setClearDialogOpen(false);
      onRefresh();
      fetchRoomData();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No animals found in this sub-room");
      } else {
        toast.error("Failed to clear sub-room");
      }
      console.error(error);
    } finally {
      setClearingRoom(false);
    }
  };

  const handleAnimalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAnimal) {
        await axios.put(`${API}/animals/${editingAnimal.id}`, animalFormData, { headers: getAuthHeaders() });
        toast.success("Animal updated successfully");
      } else {
        await axios.post(`${API}/animals`, animalFormData, { headers: getAuthHeaders() });
        toast.success("Animal added successfully");
      }
      setAnimalDialogOpen(false);
      onRefresh();
      fetchRoomData();
    } catch (error) {
      toast.error("Failed to save animal");
    }
  };

  const handleAnimalPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setAnimalFormData({...animalFormData, photo: reader.result});
    };
    reader.readAsDataURL(file);
  };

  const getHealthBadgeColor = (status) => {
    switch(status) {
      case "healthy": return "bg-green-500";
      case "sick": return "bg-red-500";
      case "under_treatment": return "bg-yellow-500";
      default: return "bg-gray-500";
    }
  };

  if (loading) {
    return <div className="p-4">Loading room data...</div>;
  }

  const roomAnimals = getRoomAnimals();

  return (
    <div className="single-sub-room-view p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" onClick={onClose}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Cages
        </Button>
        <div>
          <h2 className="text-2xl font-bold">Cage {cageNumber.replace('C-', '')} - Room {roomLabel}</h2>
          <p className="text-sm text-gray-600">{roomAnimals.length} / {roomData?.capacity || 10} animals</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Room Details Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Room Details</CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingRoom(!editingRoom)}
              >
                <Edit className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {editingRoom ? (
              <div className="space-y-3">
                <div>
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={tempRoomData.capacity}
                    onChange={(e) => setTempRoomData({...tempRoomData, capacity: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Temperature</Label>
                  <Input
                    value={tempRoomData.temperature}
                    onChange={(e) => setTempRoomData({...tempRoomData, temperature: e.target.value})}
                    placeholder="e.g., 22°C"
                  />
                </div>
                <div>
                  <Label>Humidity</Label>
                  <Input
                    value={tempRoomData.humidity}
                    onChange={(e) => setTempRoomData({...tempRoomData, humidity: e.target.value})}
                    placeholder="e.g., 60%"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={tempRoomData.notes}
                    onChange={(e) => setTempRoomData({...tempRoomData, notes: e.target.value})}
                    placeholder="Any additional notes..."
                  />
                </div>
                <Button onClick={handleSaveRoomDetails} className="w-full">Save Details</Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div><strong>Capacity:</strong> {roomData?.capacity || 10} animals</div>
                {roomData?.temperature && <div><strong>Temperature:</strong> {roomData.temperature}</div>}
                {roomData?.humidity && <div><strong>Humidity:</strong> {roomData.humidity}</div>}
                {roomData?.notes && (
                  <div>
                    <strong>Notes:</strong>
                    <p className="text-sm text-gray-600 mt-1">{roomData.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Animals List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Animals in this Room</CardTitle>
              <div className="flex gap-2">
                {roomAnimals.length > 0 && (
                  <Button 
                    variant="destructive" 
                    onClick={() => setClearDialogOpen(true)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Room
                  </Button>
                )}
                <Button onClick={handleAddAnimal}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Animal
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {roomAnimals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No animals in this room yet</p>
                <Button onClick={handleAddAnimal} variant="outline" className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Animal
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {roomAnimals.map(animal => (
                  <Card key={animal.id} className="border-2">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {animal.photo && (
                          <img
                            src={animal.photo}
                            alt={animal.breed}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-bold text-lg">{animal.breed}</h3>
                              <p className="text-sm text-gray-600 capitalize">
                                {animal.animal_type.replace('_', ' ')} • {animal.gender} • {animal.age} months
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMoveAnimal(animal)}
                                title="Move to another sub-room"
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              >
                                <ArrowRightLeft className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditAnimal(animal)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteAnimal(animal.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                            <div><strong>Weight:</strong> {animal.weight} kg</div>
                            <div className="flex items-center gap-1">
                              <strong>Group:</strong> 
                              <span 
                                className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: GROUP_COLORS[animal.group]?.bg || '#6B7280' }}
                              >
                                {GROUP_COLORS[animal.group]?.label || animal.group}
                              </span>
                            </div>
                            {animal.birth_date && <div><strong>Birth Date:</strong> {animal.birth_date}</div>}
                            {animal.purchase_date && <div><strong>Purchase Date:</strong> {animal.purchase_date}</div>}
                          </div>
                          <div className="mt-2">
                            <Badge className={getHealthBadgeColor(animal.health_status)}>
                              {animal.health_status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Animal Form Dialog */}
      {animalDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingAnimal ? "Edit Animal" : "Add New Animal"}</CardTitle>
              <CardDescription>
                {editingAnimal ? "Update animal details" : `Add animal to Cage ${cageNumber.replace('C-', '')} - Room ${roomLabel}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAnimalSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Animal Type</Label>
                    <Select 
                      value={animalFormData.animal_type} 
                      onValueChange={(value) => setAnimalFormData({...animalFormData, animal_type: value})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rabbit">Rabbit</SelectItem>
                        <SelectItem value="grass_cutter">Grass Cutter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Breed</Label>
                    <Input
                      value={animalFormData.breed}
                      onChange={(e) => setAnimalFormData({...animalFormData, breed: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Age (months) {animalFormData.birth_date && <span className="text-green-600 text-xs">(auto-calculated)</span>}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="e.g., 1.5, 5.6"
                      value={animalFormData.age}
                      onChange={(e) => setAnimalFormData({...animalFormData, age: e.target.value})}
                      required
                      readOnly={!!animalFormData.birth_date}
                      className={animalFormData.birth_date ? "bg-gray-100" : ""}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Gender</Label>
                    <Select 
                      value={animalFormData.gender} 
                      onValueChange={(value) => setAnimalFormData({...animalFormData, gender: value})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="e.g., 1.5, 5.6"
                      value={animalFormData.weight}
                      onChange={(e) => setAnimalFormData({...animalFormData, weight: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Health Status</Label>
                    <Select 
                      value={animalFormData.health_status} 
                      onValueChange={(value) => setAnimalFormData({...animalFormData, health_status: value})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="healthy">Healthy</SelectItem>
                        <SelectItem value="under_treatment">Under Treatment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Purchase Date <span className="text-gray-400 text-xs">(optional)</span></Label>
                    <Input
                      type="date"
                      value={animalFormData.purchase_date}
                      onChange={(e) => setAnimalFormData({...animalFormData, purchase_date: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Birth Date</Label>
                    <Input
                      type="date"
                      value={animalFormData.birth_date}
                      onChange={(e) => {
                        const newBirthDate = e.target.value;
                        const calculatedAge = calculateAgeFromBirthDate(newBirthDate);
                        setAnimalFormData({
                          ...animalFormData, 
                          birth_date: newBirthDate,
                          age: calculatedAge || animalFormData.age
                        });
                      }}
                    />
                    {animalFormData.birth_date && (
                      <p className="text-xs text-green-600">Age auto-calculated from birth date</p>
                    )}
                  </div>

                  <div className="space-y-2 col-span-2">
                    <Label>Group</Label>
                    <Select 
                      value={animalFormData.group} 
                      onValueChange={(value) => setAnimalFormData({...animalFormData, group: value})}
                    >
                      <SelectTrigger><SelectValue placeholder="Select a group" /></SelectTrigger>
                      <SelectContent className="max-h-[280px] overflow-y-auto">
                        <SelectItem value="stock">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#3B82F6' }} />
                            Stock
                          </div>
                        </SelectItem>
                        <SelectItem value="babies">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#EC4899' }} />
                            Babies
                          </div>
                        </SelectItem>
                        <SelectItem value="mothers">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#8B5CF6' }} />
                            Mothers
                          </div>
                        </SelectItem>
                        <SelectItem value="crossed">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#F59E0B' }} />
                            Crossed
                          </div>
                        </SelectItem>
                        <SelectItem value="pregnant">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#EF4444' }} />
                            Pregnant
                          </div>
                        </SelectItem>
                        <SelectItem value="weaning">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#14B8A6' }} />
                            Weaning
                          </div>
                        </SelectItem>
                        <SelectItem value="sold">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#6B7280' }} />
                            Sold
                          </div>
                        </SelectItem>
                        <SelectItem value="ready_to_sell">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: '#10B981' }} />
                            Ready to Sell
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Color indicator for selected group */}
                    {animalFormData.group && GROUP_COLORS[animalFormData.group] && (
                      <div 
                        className="mt-2 px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-2 text-white"
                        style={{ backgroundColor: GROUP_COLORS[animalFormData.group].bg }}
                      >
                        <span className="w-2 h-2 rounded-full bg-white"></span>
                        {GROUP_COLORS[animalFormData.group].label}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 col-span-2">
                    <Label>Animal Photo</Label>
                    <input
                      type="file"
                      ref={animalPhotoRef}
                      accept="image/*"
                      className="hidden"
                      onChange={handleAnimalPhotoSelect}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => animalPhotoRef.current?.click()}
                      className="w-full"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      {animalFormData.photo ? "Change Photo" : "Upload Photo"}
                    </Button>
                    {animalFormData.photo && (
                      <div className="relative inline-block mt-2">
                        <img
                          src={animalFormData.photo}
                          alt="Animal preview"
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute top-1 right-1"
                          onClick={() => setAnimalFormData({...animalFormData, photo: ""})}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setAnimalDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingAnimal ? "Update" : "Add"} Animal
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Move Animal Dialog */}
      {moveDialogOpen && animalToMove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                <CardTitle>Move Animal</CardTitle>
              </div>
              <CardDescription>
                Move "{animalToMove.breed}" to a different sub-room
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Current Location */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Current Location:</p>
                <p className="font-semibold">{animalToMove.cage_number} - Room {animalToMove.sub_room}</p>
              </div>

              {/* Animal Info */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <span 
                    className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: GROUP_COLORS[animalToMove.group]?.bg || '#6B7280' }}
                  >
                    {GROUP_COLORS[animalToMove.group]?.label || animalToMove.group}
                  </span>
                  <span className="font-medium">{animalToMove.breed}</span>
                  <span className="text-sm text-gray-500">({animalToMove.animal_type})</span>
                </div>
              </div>

              {/* Destination Selection */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Destination Cage *</Label>
                  <Select 
                    value={moveDestination.cage_number} 
                    onValueChange={handleCageChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select cage..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CAGES.map(cage => {
                        const emptyCount = getEmptySubRooms(cage).length;
                        return (
                          <SelectItem key={cage} value={cage} disabled={emptyCount === 0}>
                            {cage} {cage === cageNumber && '(current)'} - {emptyCount} empty room(s)
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Destination Sub-Room * <span className="text-xs text-gray-500">(only empty rooms shown)</span></Label>
                  <Select 
                    value={moveDestination.sub_room} 
                    onValueChange={(value) => setMoveDestination({...moveDestination, sub_room: value})}
                    disabled={!moveDestination.cage_number}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={moveDestination.cage_number ? "Select empty sub-room..." : "Select cage first..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSubRooms.length === 0 ? (
                        <SelectItem value="none" disabled>No empty rooms available</SelectItem>
                      ) : (
                        availableSubRooms.map(room => (
                          <SelectItem key={room} value={room}>
                            Room {room} (Empty)
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {moveDestination.cage_number && availableSubRooms.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">No empty sub-rooms in this cage. Choose another cage.</p>
                  )}
                </div>
              </div>

              {/* New Location Preview */}
              {moveDestination.cage_number && moveDestination.sub_room && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700 mb-1">New Location:</p>
                  <p className="font-semibold text-green-800">
                    {moveDestination.cage_number} - Room {moveDestination.sub_room}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setMoveDialogOpen(false);
                    setAnimalToMove(null);
                    // Notify parent that move dialog is closed
                    if (onMoveDialogChange) onMoveDialogChange(false);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleMoveSubmit}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Move Animal
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Clear Sub-Room Confirmation Dialog */}
      {clearDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-6 h-6" />
                <CardTitle>Clear Sub-Room</CardTitle>
              </div>
              <CardDescription>
                This action cannot be undone
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium mb-2">
                  You are about to permanently delete {roomAnimals.length} animal{roomAnimals.length !== 1 ? 's' : ''} from this room:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside max-h-32 overflow-y-auto">
                  {roomAnimals.map(animal => (
                    <li key={animal.id}>
                      {animal.breed} ({animal.animal_type.replace('_', ' ')})
                    </li>
                  ))}
                </ul>
              </div>
              
              {!isAdmin && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> As a non-admin user, this request will be sent to the administrator for approval.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setClearDialogOpen(false)}
                  disabled={clearingRoom}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleClearSubRoom}
                  disabled={clearingRoom}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {clearingRoom ? (
                    <>Processing...</>
                  ) : isAdmin ? (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear Now
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Request Approval
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SingleSubRoom;
